import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuthenticationConfirmationsResendEmailConfirmationMutation } from '@lib/graphql-common';
import {
  AuthenticationPasswordsUpdateInput,
  AuthenticationScopeEnum,
  useAuthenticationPasswordsForgotMutation,
  useAuthenticationPasswordsUpdateMutation,
  useCountriesQuery,
  UserSelfUpdateInputObject,
  useUserSelfUpdateMutation,
  WhoamiDocument,
} from 'graphql-common';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import _cloneDeep from 'lodash/cloneDeep';
import { prepareAttachedField } from '@lib/utils/prepareAttachedField';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage, getGraphQLErrors } from '@lib/utils/graphQLErrors';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import getAppUrlWithDomain from '@lib/utils/getAppUrlWithDomain';
import { Values } from '@lib/interfaces/form';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import { APP_URLS } from 'constants/urls';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import getPhoneNumberPreparedDataFromValues from '@lib/utils/getPhoneNumberPreparedDataFromValues';
import ProfileForm from './components/ProfileForm';
import getDefaultValuesFromQueryData from './utils/getDefaultValuesFromQueryData';
import LangForm from './components/LangForm';
import PasswordForm from './components/PasswordForm';
import styles from './ProfileContainer.module.scss';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

function ProfileContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const {
    currentUser,
    currentUserId,
    currentUserEmail,
    currentUserPasswordUpdatedAt,
    currentUserUnconfirmedEmail,
  } = useWhoamiQueryHook();

  const countriesQuery = useCountriesQuery({
    fetchPolicy: 'cache-first',
  });
  const countries = _get(countriesQuery, 'data.data', []);
  const countriesCodes = countries.map(({ code }) => code.toLowerCase());

  // const { onLogoutHandler }: ContextProps = useOutletContext();

  const [updateMutation, updateMutationResult] = useUserSelfUpdateMutation({
    onCompleted: () => {
      toast({ content: t('update-admin-success') });
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
    update: (cache, result) => {
      try {
        const cacheData = _cloneDeep(
          cache.readQuery({
            query: WhoamiDocument,
          }),
        );
        const data = _get(result, 'data.data', {});
        const existingData = _get(cacheData, 'whoami', {});
        if (existingData && data) {
          const updatedData = {
            // @ts-ignore
            ...cacheData,
            whoami: {
              ...existingData,
              ...data,
            },
          };
          cache.writeQuery({
            query: WhoamiDocument,
            data: updatedData,
          });
        }
      } catch (e) {
        console.warn(e);
      }
    },
  });

  const [resendConfirmationMutation] =
    useAuthenticationConfirmationsResendEmailConfirmationMutation({
      onCompleted: (response) => {
        const content =
          response?.data?.message || t('resend-confirmation-success');
        toast({ content });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });

  const [sendPasswordResetMutation] = useAuthenticationPasswordsForgotMutation({
    onCompleted: (response) => {
      const message = _get(response, 'data.message', '');
      toast({ content: message });
    },
    onError: (error) => {
      toast({ content: getErrorMessage(error, t) });
    },
  });

  const [passwordResetMutation, passwordResetMutationResult] =
    useAuthenticationPasswordsUpdateMutation({
      refetchQueries: [WhoamiDocument],
      onCompleted: () => {
        toast({ content: t('reset-password-success') });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });

  // const [destroyMutation] = useUserDestroyMutation({
  //   onCompleted: () => {
  //     onLogoutHandler();
  //   },
  //   onError: (error) => {
  //     toast({ content: error.message || 'Oops! Something went wrong' });
  //   },
  // });

  const { error: updateMutationError, loading: updateMutationLoading } =
    updateMutationResult;
  const graphQLErrors = getGraphQLErrors(updateMutationError);

  const {
    error: passwordResetMutationError,
    loading: passwordResetMutationLoading,
    reset: passwordResetMutationReset,
  } = passwordResetMutationResult;
  const passwordResetGraphQLErrors = getGraphQLErrors(
    passwordResetMutationError,
  );

  const onUpdateHandler = (values: Values) => {
    const attributes = _pick(values, [
      'cancelConfirmation',
      'passwordChallenge',
      'email',
      'firstName',
      'phoneNumber',
      'personalId',
      'lang',
      'lastName',
      'password',
      'passwordConfirmation',
    ]) as UserSelfUpdateInputObject;
    attributes.phoneNumber = getPhoneNumberPreparedDataFromValues(values);
    const avatarAttached = prepareAttachedField(values.avatarAttached);
    if (avatarAttached !== undefined) {
      attributes.avatarAttached = avatarAttached;
    }
    return updateMutation({
      variables: {
        input: {
          attributes,
          redirectUrl: getAppUrlWithDomain(
            APP_URLS.setup.confirmEmail.path,
            AUTH_PLATFORM,
          ),
        },
      },
    });
  };

  const onUpdatePasswordHandler = (values: Values) => {
    const attributes = _pick(values, [
      'passwordChallenge',
      'password',
      'passwordConfirmation',
    ]) as AuthenticationPasswordsUpdateInput;
    return passwordResetMutation({
      variables: {
        input: attributes,
      },
    });
  };

  // const onDestroyHandler = () => destroyMutation(
  //   {
  //     variables:
  //       {
  //         id: currentUserId,
  //       },
  //   },
  // );

  // const onConfirmedDelete = () => {
  //   toast({
  //     content: t('destroy-admin-success'),
  //     closeCallback: onDestroyHandler,
  //   });
  // };

  const onResendConfirmationHandler = () =>
    resendConfirmationMutation({
      variables: {
        input: {
          id: currentUserId,
          redirectUrl: getAppUrlWithDomain(
            APP_URLS.setup.confirmEmail.path,
            AUTH_PLATFORM,
          ),
          scope: AuthenticationScopeEnum.User,
        },
      },
    });

  const onSendPasswordResetHandler = () =>
    sendPasswordResetMutation({
      variables: {
        input: {
          email: currentUserEmail || '',
          redirectUrl: getAppUrlWithDomain(
            APP_URLS.auth.resetPassword.path,
            AUTH_PLATFORM,
          ),
          scope: AuthenticationScopeEnum.User,
        },
      },
    });

  const defaultValues = getDefaultValuesFromQueryData(
    currentUser as unknown as Values,
  );

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageBody withoutHeader>
        <div className={styles.body}>
          <ProfileForm
            countriesCodes={countriesCodes}
            defaultValues={defaultValues}
            graphQLErrors={graphQLErrors}
            loading={updateMutationLoading}
            onResendConfirmationHandler={onResendConfirmationHandler}
            onSubmit={onUpdateHandler}
            unconfirmedEmail={currentUserUnconfirmedEmail}
          />
          <LangForm
            defaultValues={currentUser as unknown as Values}
            loading={updateMutationLoading}
            onSubmit={onUpdateHandler}
          />
          <PasswordForm
            graphQLErrors={passwordResetGraphQLErrors}
            loading={passwordResetMutationLoading}
            onSendPasswordResetHandler={onSendPasswordResetHandler}
            onSubmit={onUpdatePasswordHandler}
            passwordUpdatedAt={currentUserPasswordUpdatedAt}
            updateMutationReset={passwordResetMutationReset}
          />
          {/* <DeleteProfileForm */}
          {/*  onConfirmedDelete={onConfirmedDelete} */}
          {/* /> */}
        </div>
      </PageBody>
    </>
  );
}

export default ProfileContainer;
